import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { Shield, CheckCircle, Loader2, CreditCard, MapPin, Mail } from 'lucide-react';

const PAYPAL_CLIENT_ID = 'AQN6CD7Dlqw6cYWgrgCJ6qP-33JD7lKEWI1NZ9O1yIyDEBf1EQcbgiEH4W1_Y214Yd0mX8ZxnBM8cV3O';

// Canadian Provinces with tax info
const PROVINCES = [
  { code: 'AB', name: 'Alberta', rate: 0.05, type: 'GST' },
  { code: 'BC', name: 'British Columbia', rate: 0.05, type: 'GST' },
  { code: 'MB', name: 'Manitoba', rate: 0.05, type: 'GST' },
  { code: 'NB', name: 'New Brunswick', rate: 0.15, type: 'HST' },
  { code: 'NL', name: 'Newfoundland and Labrador', rate: 0.15, type: 'HST' },
  { code: 'NS', name: 'Nova Scotia', rate: 0.15, type: 'HST' },
  { code: 'NT', name: 'Northwest Territories', rate: 0.05, type: 'GST' },
  { code: 'NU', name: 'Nunavut', rate: 0.05, type: 'GST' },
  { code: 'ON', name: 'Ontario', rate: 0.13, type: 'HST' },
  { code: 'PE', name: 'Prince Edward Island', rate: 0.15, type: 'HST' },
  { code: 'QC', name: 'Quebec', rate: 0.14975, type: 'GST+QST', gst: 0.05, qst: 0.09975 },
  { code: 'SK', name: 'Saskatchewan', rate: 0.05, type: 'GST' },
  { code: 'YT', name: 'Yukon', rate: 0.05, type: 'GST' },
];

const CheckoutPage = () => {
  const { cart, cartId, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [formValid, setFormValid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [taxBreakdown, setTaxBreakdown] = useState(null);
  const [calculatingTax, setCalculatingTax] = useState(false);
  
  // Shipping settings from backend
  const [shippingSettings, setShippingSettings] = useState({
    shipping_free_threshold: 0,
    shipping_flat_rate: 0,
    currency: 'CAD',
    shipping_enabled: false  // Track if shipping is enabled
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: 'ON',
    postal_code: '',
    country: 'Canada',
    phone: ''
  });

  const [billingAddress, setBillingAddress] = useState({
    same_as_shipping: true
  });
  
  const [customerEmail, setCustomerEmail] = useState('');

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  // Fetch shipping settings on mount
  useEffect(() => {
    const fetchShippingSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/shop/shipping-settings`);
        if (response.ok) {
          const data = await response.json();
          // Check if shipping is enabled (threshold > 0 AND flat_rate > 0)
          const shippingEnabled = data.shipping_free_threshold > 0 && data.shipping_flat_rate > 0;
          setShippingSettings({
            ...data,
            shipping_enabled: shippingEnabled
          });
        }
      } catch (error) {
        console.error('Error fetching shipping settings:', error);
      }
    };
    fetchShippingSettings();
  }, [API_BASE]);

  useEffect(() => {
    if (!cart.items || cart.items.length === 0) {
      navigate('/cart');
      return;
    }
  }, [cart, cartId, navigate]);

  useEffect(() => {
    // Email regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = customerEmail && emailRegex.test(customerEmail);
    
    const isValid = shippingAddress.first_name && 
                    shippingAddress.last_name && 
                    shippingAddress.address_line1 && 
                    shippingAddress.city && 
                    shippingAddress.state_province && 
                    shippingAddress.postal_code &&
                    shippingAddress.phone &&
                    isEmailValid;
    setFormValid(isValid);
  }, [shippingAddress, customerEmail]);

  // Calculate tax when province changes
  useEffect(() => {
    if (cart.subtotal && shippingAddress.state_province) {
      calculateTax();
    }
    // Recalculate tax only when the taxable amount or province changes.
  }, [cart.subtotal, shippingAddress.state_province, discountAmount]);

  const calculateTax = async () => {
    setCalculatingTax(true);
    try {
      const response = await fetch(`${API_BASE}/api/tax/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: cart.subtotal - discountAmount,
          province: shippingAddress.state_province
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTaxBreakdown(data);
      } else {
        // Fallback calculation
        const province = PROVINCES.find(p => p.code === shippingAddress.state_province);
        const subtotal = cart.subtotal - discountAmount;
        const taxAmount = subtotal * (province?.rate || 0.13);
        setTaxBreakdown({
          subtotal: subtotal,
          total_tax_amount: taxAmount,
          total_with_tax: subtotal + taxAmount,
          tax_type: province?.type || 'HST',
          hst_amount: province?.type === 'HST' ? taxAmount : 0,
          hst_rate: province?.type === 'HST' ? province.rate : 0,
          gst_amount: province?.type !== 'HST' ? subtotal * 0.05 : 0,
          gst_rate: province?.type !== 'HST' ? 0.05 : 0,
          qst_amount: province?.code === 'QC' ? subtotal * 0.09975 : 0,
          qst_rate: province?.code === 'QC' ? 0.09975 : 0,
        });
      }
    } catch (error) {
      console.error('Error calculating tax:', error);
    } finally {
      setCalculatingTax(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/coupons/validate?code=${couponCode}&amount=${cart.subtotal}&coupon_type=shop`);

      if (response.ok) {
        const data = await response.json();
        setCouponApplied(data.coupon);
        setDiscountAmount(data.coupon.discount_amount);
        toast({
          title: 'Coupon Applied!',
          description: `You save $${data.coupon.discount_amount.toFixed(2)} CAD`,
        });
      } else {
        toast({
          title: 'Invalid Coupon',
          description: 'This coupon code is not valid',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
    }
  };

  // PayPal Functions
  const createOrder = async () => {
    if (!formValid) {
      toast({ title: 'Eksik Bilgi', description: 'Lütfen tüm gerekli alanları doldurun', variant: 'destructive' });
      throw new Error('Form not valid');
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({ title: 'Giriş Gerekli', description: 'Satın alma işlemini tamamlamak için giriş yapın', variant: 'destructive' });
        navigate('/login');
        throw new Error('Not logged in');
      }

      const response = await fetch(`${API_BASE}/api/shop/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          cart_id: cartId,
          shipping_address: shippingAddress,
          billing_address: billingAddress.same_as_shipping ? shippingAddress : billingAddress,
          coupon_code: couponApplied ? couponCode : null,
          customer_email: customerEmail
        })
      });

      const data = await response.json();
      if (!response.ok) {
        // Handle stock validation errors specifically
        if (data.detail && data.detail.includes('Stock validation failed')) {
          toast({ 
            title: 'Stok Hatası', 
            description: 'Sepetinizdeki bazı ürünlerin stoku yetersiz. Lütfen sepetinizi kontrol edin.',
            variant: 'destructive' 
          });
          navigate('/cart');
          throw new Error('Stock validation failed');
        }
        throw new Error(data.detail || 'Sipariş oluşturulamadı');
      }

      const approvalUrl = data.approval_url;
      const urlParams = new URLSearchParams(approvalUrl.split('?')[1]);
      return urlParams.get('token') || data.payment_id;
    } catch (error) {
      toast({ title: 'Hata', description: error.message, variant: 'destructive' });
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/shop/payments/execute?payment_id=${data.paymentID || data.orderID}&payer_id=${data.payerID}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Payment execution failed');

      clearCart();
      toast({ title: 'Order Placed Successfully! 🎉', description: `Order #${result.order_number} has been confirmed.` });
      navigate('/orders');
    } catch (error) {
      toast({ title: 'Payment Failed', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const onError = (err) => {
    toast({ title: 'Payment Error', description: 'Something went wrong with PayPal.', variant: 'destructive' });
  };

  const onCancel = () => {
    toast({ title: 'Payment Cancelled', description: 'You cancelled the payment.' });
  };

  // Stripe Function
  const handleStripePayment = async () => {
    if (!formValid) {
      toast({ title: 'Eksik Bilgi', description: 'Lütfen tüm gerekli alanları doldurun', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast({ title: 'Giriş Gerekli', description: 'Lütfen giriş yapın', variant: 'destructive' });
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          package_id: cartId,
          package_type: 'shop',
          origin_url: window.location.origin,
          cart_id: cartId,
          shipping_address: shippingAddress,
          billing_address: billingAddress.same_as_shipping ? shippingAddress : billingAddress,
          coupon_code: couponApplied ? couponCode : null,
          province: shippingAddress.state_province,
          customer_email: customerEmail
        })
      });

      const data = await response.json();
      if (!response.ok) {
        // Handle stock validation errors specifically
        if (data.detail && data.detail.includes('Stock validation failed')) {
          toast({ 
            title: 'Stok Hatası', 
            description: 'Sepetinizdeki bazı ürünlerin stoku yetersiz. Lütfen sepetinizi kontrol edin.',
            variant: 'destructive' 
          });
          navigate('/cart');
          return;
        }
        throw new Error(data.detail || 'Ödeme oluşturulamadı');
      }

      window.location.href = data.checkout_url;
    } catch (error) {
      toast({ title: 'Hata', description: error.message, variant: 'destructive' });
      setLoading(false);
    }
  };

  // Calculate totals - use dynamic shipping settings from backend (single source of truth)
  const subtotal = cart.subtotal || 0;
  
  // Determine shipping cost based on backend settings
  let shippingCost = 0;
  let qualifiesForFreeShipping = false;
  
  if (shippingSettings.shipping_enabled) {
    // Shipping is enabled - check threshold
    qualifiesForFreeShipping = subtotal >= shippingSettings.shipping_free_threshold;
    shippingCost = qualifiesForFreeShipping ? 0 : shippingSettings.shipping_flat_rate;
  } else {
    // Shipping is disabled - no shipping cost
    qualifiesForFreeShipping = true;
    shippingCost = 0;
  }
  
  const taxAmount = taxBreakdown?.total_tax_amount || (subtotal - discountAmount) * 0.13;
  const total = (subtotal - discountAmount) + shippingCost + taxAmount;

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="dark:text-gray-300 mb-4">Your cart is empty</p>
          <Button onClick={() => navigate('/shop')}>Continue Shopping</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-3xl font-bold mb-8 dark:text-white">Checkout</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="dark:text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" />Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label className="dark:text-gray-300">Email Address *</Label>
                  <Input 
                    type="email"
                    value={customerEmail} 
                    onChange={(e) => setCustomerEmail(e.target.value)} 
                    placeholder="your@email.com" 
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                    data-testid="checkout-email-input"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Order confirmation and updates will be sent to this email</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="dark:text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5" />Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="dark:text-gray-300">First Name *</Label>
                    <Input value={shippingAddress.first_name} onChange={(e) => setShippingAddress({...shippingAddress, first_name: e.target.value})} placeholder="John" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Last Name *</Label>
                    <Input value={shippingAddress.last_name} onChange={(e) => setShippingAddress({...shippingAddress, last_name: e.target.value})} placeholder="Doe" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  </div>
                </div>
                
                <div>
                  <Label className="dark:text-gray-300">Address Line 1 *</Label>
                  <Input value={shippingAddress.address_line1} onChange={(e) => setShippingAddress({...shippingAddress, address_line1: e.target.value})} placeholder="123 Main Street" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                
                <div>
                  <Label className="dark:text-gray-300">Address Line 2</Label>
                  <Input value={shippingAddress.address_line2} onChange={(e) => setShippingAddress({...shippingAddress, address_line2: e.target.value})} placeholder="Apt, Suite (optional)" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="dark:text-gray-300">City *</Label>
                    <Input value={shippingAddress.city} onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})} placeholder="Toronto" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Province *</Label>
                    <select
                      value={shippingAddress.state_province}
                      onChange={(e) => setShippingAddress({...shippingAddress, state_province: e.target.value})}
                      className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      {PROVINCES.map(province => (
                        <option key={province.code} value={province.code}>
                          {province.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="dark:text-gray-300">Postal Code *</Label>
                    <Input value={shippingAddress.postal_code} onChange={(e) => setShippingAddress({...shippingAddress, postal_code: e.target.value})} placeholder="M5V 1A1" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Phone *</Label>
                    <Input value={shippingAddress.phone} onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})} placeholder="+1 (555) 123-4567" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader><CardTitle className="dark:text-white">Billing Address</CardTitle></CardHeader>
              <CardContent>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={billingAddress.same_as_shipping} onChange={(e) => setBillingAddress({...billingAddress, same_as_shipping: e.target.checked})} className="rounded" />
                  <span className="text-sm dark:text-gray-300">Same as shipping address</span>
                </label>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader><CardTitle className="dark:text-white">Order Summary</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Price Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                    <span className="dark:text-white">${subtotal.toFixed(2)} CAD</span>
                  </div>
                  
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-${discountAmount.toFixed(2)} CAD</span>
                    </div>
                  )}
                  
                  {/* Shipping - Only show if shipping is enabled */}
                  {shippingSettings.shipping_enabled && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Shipping</span>
                        <span className="dark:text-white">{shippingCost === 0 ? <span className="text-green-600 font-semibold">FREE</span> : `$${shippingCost.toFixed(2)} CAD`}</span>
                      </div>
                      
                      {/* Shipping Info Message */}
                      <div className="text-xs px-2 py-1.5 rounded bg-gray-50 dark:bg-gray-700/50">
                        {qualifiesForFreeShipping ? (
                          <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            You qualify for free shipping!
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">
                            Orders over ${shippingSettings.shipping_free_threshold?.toFixed(0)} qualify for free shipping.
                          </span>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* Tax Breakdown */}
                  {calculatingTax ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Tax</span>
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  ) : taxBreakdown && (
                    <>
                      {taxBreakdown.hst_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">HST ({(taxBreakdown.hst_rate * 100).toFixed(0)}%)</span>
                          <span className="dark:text-white">${taxBreakdown.hst_amount.toFixed(2)} CAD</span>
                        </div>
                      )}
                      {taxBreakdown.gst_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">GST ({(taxBreakdown.gst_rate * 100).toFixed(0)}%)</span>
                          <span className="dark:text-white">${taxBreakdown.gst_amount.toFixed(2)} CAD</span>
                        </div>
                      )}
                      {taxBreakdown.qst_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">QST ({(taxBreakdown.qst_rate * 100).toFixed(3)}%)</span>
                          <span className="dark:text-white">${taxBreakdown.qst_amount.toFixed(2)} CAD</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="border-t dark:border-gray-600 pt-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span className="dark:text-white">Total</span>
                      <span className="text-red-600">${total.toFixed(2)} CAD</span>
                    </div>
                  </div>
                </div>

                {/* Coupon */}
                <div className="space-y-2 border-t dark:border-gray-600 pt-4">
                  <Label className="text-sm font-semibold dark:text-gray-300">Coupon Code</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Enter code" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} disabled={couponApplied !== null} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    <Button onClick={handleApplyCoupon} variant="outline" disabled={couponApplied !== null || !couponCode.trim()}>{couponApplied ? 'Applied' : 'Apply'}</Button>
                  </div>
                  {couponApplied && <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Coupon applied!</p>}
                </div>

                {/* Free shipping banner - only show if shipping is enabled and free */}
                {shippingSettings.shipping_enabled && shippingCost === 0 && (
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
                    🎉 FREE shipping!
                  </div>
                )}

                {/* Items */}
                <div className="space-y-2 border-t dark:border-gray-600 pt-4">
                  <h3 className="font-semibold text-sm dark:text-white">Items ({cart.item_count})</h3>
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{item.product_title} × {item.quantity}</span>
                      <span className="dark:text-white">${(item.price * item.quantity).toFixed(2)} CAD</span>
                    </div>
                  ))}
                </div>

                {/* Payment */}
                <div className="border-t dark:border-gray-600 pt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-green-500" />
                    <span className="font-semibold dark:text-white">Secure Payment</span>
                  </div>
                  
                  {loading ? (
                    <div className="flex flex-col items-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-red-600 mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Processing...</p>
                    </div>
                  ) : (
                    <>
                      {!formValid && (
                        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
                          Please fill in all required shipping fields.
                        </div>
                      )}

                      {/* Payment Method */}
                      <div className="space-y-3 mb-4">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Method:</p>
                        <div className="grid grid-cols-2 gap-3">
                          <button onClick={() => setPaymentMethod('paypal')} className={`p-3 rounded-lg border-2 transition-all ${paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                            <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" className="h-5 mx-auto" />
                          </button>
                          <button onClick={() => setPaymentMethod('stripe')} className={`p-3 rounded-lg border-2 transition-all ${paymentMethod === 'stripe' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                            <div className="flex flex-col items-center justify-center gap-1">
                              <CreditCard className="w-5 h-5 text-purple-600" />
                              <span className="font-semibold text-purple-600 text-xs">Debit or Credit Card</span>
                            </div>
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        {paymentMethod === 'paypal' ? (
                          <>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">Pay with PayPal</p>
                            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'CAD' }}>
                              <PayPalButtons style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }} disabled={!formValid} createOrder={createOrder} onApprove={onApprove} onError={onError} onCancel={onCancel} />
                            </PayPalScriptProvider>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">Pay with Debit or Credit Card</p>
                            <Button onClick={handleStripePayment} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5" disabled={!formValid || loading}>
                              <CreditCard className="w-5 h-5 mr-2" />Pay ${total.toFixed(2)} CAD
                            </Button>
                            <div className="flex items-center justify-center gap-1.5 mt-3">
                              <Shield className="w-4 h-4 text-[#635BFF]" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">Powered by</span>
                              <span className="text-xs font-bold text-[#635BFF]">Stripe</span>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-4">
                        <Shield className="w-4 h-4" /><span>Secure & encrypted</span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
